import { createSearchReferenceResolver } from './createSearchReferenceResolver';
import { getExternalOptionsByIdFetchQueryOptions, getExternalOptionsByIdFetchOptions, getExternalOptionSearchFetchOptions, makeExternalOptionsByIdUrl, makeExternalOptionsSearchUrl } from './internals/externalOptionsResolver/api';
import { makeExternalOptionsByIdUrl as getV3ByIdUrl, makeExternalOptionsSearchUrl as getV3SearchUrl, getExternalOptionsFetchQueryOptions as getV3QueryOptions, getExternalOptionsByIdFetchOptions as getV3ByIdFetchOptions, getExternalOptionSearchFetchOptions as getV3SearchFetchOptions } from './internals/externalOptionsResolver/v3Api';
import { formatExternalOptionsByIdResponse, formatExternalOptionsSearchResponse } from './internals/externalOptionsResolver/formatters';
import http from 'hub-http/clients/apiClient';

const buildStandardResolver = ({
  byIdFetchOptions,
  debouncePeriod,
  enableCaching,
  httpClient,
  maxBatchSize,
  objectTypeId,
  referenceType,
  searchFetchOptions
}) => {
  const byIdUrl = makeExternalOptionsByIdUrl(referenceType, objectTypeId);
  const searchUrl = makeExternalOptionsSearchUrl(referenceType, objectTypeId);

  const fetchByIds = ids => httpClient.post(byIdUrl, {
    query: getExternalOptionsByIdFetchQueryOptions(),
    data: getExternalOptionsByIdFetchOptions(ids, referenceType, byIdFetchOptions, objectTypeId)
  });

  const fetchBySearch = searchQuery => httpClient.get(searchUrl, {
    query: getExternalOptionSearchFetchOptions(searchQuery, searchFetchOptions)
  });

  const formatByIdResponse = byIdResponse => formatExternalOptionsByIdResponse(byIdResponse, {
    objectTypeId,
    referenceType
  });

  const formatSearchResponse = searchResponse => formatExternalOptionsSearchResponse(searchResponse, {
    objectTypeId,
    referenceType
  });

  return createSearchReferenceResolver({
    debouncePeriod,
    enableCaching,
    fetchByIds,
    fetchBySearch,
    formatByIdResponse,
    formatSearchResponse,
    maxBatchSize,
    referenceType
  });
};

const buildV3Resolver = ({
  byIdFetchOptions,
  debouncePeriod,
  enableCaching,
  httpClient,
  maxBatchSize,
  objectTypeId,
  referenceType,
  searchFetchOptions
}) => {
  const byIdUrl = getV3ByIdUrl(referenceType);
  const searchUrl = getV3SearchUrl(referenceType);

  const fetchByIds = ids => httpClient.post(byIdUrl, {
    query: getV3QueryOptions(),
    data: getV3ByIdFetchOptions(ids, referenceType, byIdFetchOptions, objectTypeId)
  });

  const fetchBySearch = searchQuery => httpClient.post(searchUrl, {
    query: getV3QueryOptions(),
    data: getV3SearchFetchOptions(referenceType, searchQuery, searchFetchOptions, objectTypeId)
  });

  const formatByIdResponse = byIdResponse => formatExternalOptionsByIdResponse(byIdResponse, {
    objectTypeId,
    referenceType
  });

  const formatSearchResponse = searchResponse => formatExternalOptionsSearchResponse(searchResponse, {
    objectTypeId,
    referenceType
  });

  return createSearchReferenceResolver({
    debouncePeriod,
    enableCaching,
    fetchByIds,
    fetchBySearch,
    formatByIdResponse,
    formatSearchResponse,
    maxBatchSize,
    referenceType
  });
};

export const createExternalOptionsReferenceResolver = ({
  // We enable deleted results by default in by id requests because it's
  // possible for deleted references to be referenced in the UI and we still
  // want to be able to resolve them. By Id requests are generally used in
  // reference to existing data that we want thte user to be able to view, they
  // are rarely, if ever, used to create new references so we don't have to
  // worry about filtering out deleted data.
  byIdFetchOptions = {
    includeDeleted: true
  },
  debouncePeriod,
  enableCaching,
  httpClient = http,
  maxBatchSize,
  objectTypeId,
  referenceType,
  // We don't enable deleted results by default in search requests because most
  // of the time users don't want to search for deleted records. Search is
  // commonly used to set new values for an external option property and
  // therefore we generally want to hide deleted references to avoid confusing
  // users
  searchFetchOptions = {
    includeDeleted: false
  },
  // Switches on the XOv3 drop-in replacement endpoints
  useV3
}) => {
  if (useV3) {
    return buildV3Resolver({
      byIdFetchOptions,
      debouncePeriod,
      enableCaching,
      httpClient,
      maxBatchSize,
      objectTypeId,
      referenceType,
      searchFetchOptions
    });
  }

  return buildStandardResolver({
    byIdFetchOptions,
    debouncePeriod,
    enableCaching,
    httpClient,
    maxBatchSize,
    objectTypeId,
    referenceType,
    searchFetchOptions
  });
};